import {
	ProductApi,
	VatTable,
	TimePeriod,
	PricingItem,
	Languages,
	ProductTypes,
} from 'common/types';
import { HOURS, DAYS, DAYS_24H, WEEKS, MONTHS, MINUTES } from 'common/constants/timePeriods';
import { newFirestoreId } from 'common/utils/newRentalUtils';
import errorHandler from 'common/services/errorHandler';
import { getDefaultVatTable } from 'common/utils/common';
import { updateLocaleField } from 'common/utils/languageUtils';
import { api } from 'common/frontend/api';
import { createLocaleField } from 'common/modules/translations';

interface CreateProductArgs {
	orderIndex: number;
	shop: {
		id: string;
		vatTable: VatTable | undefined;
		defaultLanguage: Languages;
	};
	product: {
		name: string;
		description: string;
		vatPercent: number;
		categoryIds: ProductApi['categoryIds'];
		set: boolean;
		locations: string[] | undefined;
		deliveryOptionIds: ProductApi['deliveryOptionIds'];
		rentals: {
			enabled: boolean;
			basePrice: number;
		};
		sales: {
			enabled: boolean;
			basePrice: number;
		};
		rentalTimePeriod: TimePeriod;
		additionalProduct: boolean;
		images: string[] | undefined;
	};
}

interface CloneProductArgs {
	orderIndex: number;
	shop: {
		id: string;
		defaultLanguage: Languages | undefined;
	};
	product: ProductApi;
}

interface GeneratePricingItemsArgs {
	multipliers: number[];
	basePrice: number;
	timePeriod: TimePeriod;
	timePeriodMultiplier?: number;
}

const generatePricingItems = (args: GeneratePricingItemsArgs): PricingItem[] => {
	return args.multipliers.map((multiplier) => ({
		timeValue: multiplier,
		timePeriod: args.timePeriod,
		multiplier: multiplier * (args.timePeriodMultiplier ?? 1),
		price: args.basePrice * multiplier * (args.timePeriodMultiplier ?? 1),
		channels: ['ALL'],
	}));
};

const generatePricingTable = (price: number, period?: TimePeriod): PricingItem[] => {
	switch (period) {
		case MINUTES:
			return generatePricingItems({
				multipliers: [10, 20, 30],
				basePrice: price,
				timePeriod: MINUTES,
				timePeriodMultiplier: 1,
			});
		case HOURS:
			return generatePricingItems({
				multipliers: [1, 2, 4],
				basePrice: price,
				timePeriod: HOURS,
			}).concat(
				generatePricingItems({
					multipliers: [1],
					basePrice: price,
					timePeriod: DAYS,
					timePeriodMultiplier: 8,
				}),
			);
		case DAYS:
			return generatePricingItems({
				multipliers: [1, 2, 3, 4, 5, 6],
				basePrice: price,
				timePeriod: DAYS,
			}).concat(
				generatePricingItems({
					multipliers: [1],
					basePrice: price,
					timePeriod: WEEKS,
					timePeriodMultiplier: 7,
				}),
			);

		case DAYS_24H:
			return generatePricingItems({
				multipliers: [1, 2, 3, 4, 5, 6],
				basePrice: price,
				timePeriod: DAYS_24H,
			}).concat(
				generatePricingItems({
					multipliers: [1],
					basePrice: price,
					timePeriod: WEEKS,
					timePeriodMultiplier: 7,
				}),
			);
		case WEEKS:
			return generatePricingItems({
				multipliers: [1, 2, 3, 4],
				basePrice: price,
				timePeriod: WEEKS,
			});
		case MONTHS:
			return generatePricingItems({
				multipliers: [1, 2, 3, 4],
				basePrice: price,
				timePeriod: MONTHS,
			});
		default:
			return [];
	}
};

export const createProduct = async (args: CreateProductArgs) => {
	try {
		const variantId = newFirestoreId();
		const productId = newFirestoreId();
		const name = updateLocaleField(undefined, args.product.name, args.shop.defaultLanguage);
		const product: ProductApi = {
			name,
			description: createLocaleField(args.product.description, args.shop.defaultLanguage),
			created: new Date().toISOString(),
			set: args.product.set,
			categoryIds: args.product.categoryIds,
			type: ProductTypes.RENTAL,
			id: productId,
			additionalProduct: args.product.additionalProduct,
			useSavedPricingTable: false,
			pricing: args.product.rentals.enabled
				? generatePricingTable(args.product.rentals.basePrice, args.product.rentalTimePeriod)
				: [],
			vatPercent:
				args.product.vatPercent ??
				args.shop.vatTable?.defaultVat ??
				getDefaultVatTable().defaultVat,
			additionalProductIds: [],
			collectProductCode: false,
			highlightProduct: false,
			segmentFilters: [],
			shopId: args.shop.id,
			orderIndex: args.orderIndex,
			visibleInLocations: args.product.locations ?? ['ALL'],
			sales: args.product.sales,
			rentals: args.product.rentals,
			variants: {
				properties: [],
				options: [
					{
						id: variantId,
						values: {},
						stockSources: null,
						rentals: {
							enabled: args.product.rentals.enabled,
							priceIncrease: null,
						},
						sales: {
							enabled: args.product.sales.enabled,
							priceOverride: null,
						},
					},
				],
			},
			images: args.product.images,
			...(!!args.product?.deliveryOptionIds?.length && {
				deliveryOptionIds: args.product.deliveryOptionIds,
			}),
		};
		await api().products.doc(productId).set(product, { merge: true });

		return product;
	} catch (err) {
		errorHandler.report(err);
		throw err;
	}
};

export const newVariantsFromExistingVariant = (
	existingVariant: ProductApi['variants'],
): ProductApi['variants'] => {
	const variantProperties = existingVariant.properties ?? [];
	const newProperties = variantProperties.map((p) => ({ id: newFirestoreId(), name: p.name }));
	const oldIdNewIdMap: { [oldId: string]: string } = variantProperties.reduce(
		(tot, curr, i) => ({ ...tot, [curr.id]: newProperties[i].id }),
		{},
	);
	return {
		properties: newProperties,
		options: existingVariant.options.map((variant) => {
			const values = Object.entries(variant.values).reduce(
				(tot, [id, value], index) => ({
					...tot,
					[oldIdNewIdMap[id]]: value,
				}),
				{},
			);
			return {
				...variant,
				id: newFirestoreId(),
				values,
			};
		}),
	};
};

export const cloneProduct = async (args: CloneProductArgs) => {
	try {
		const productId = newFirestoreId();
		const name = updateLocaleField(
			undefined,
			args.product.name.def + ' (copy)',
			args.shop.defaultLanguage,
		);
		const product: ProductApi = {
			...args.product,
			name,
			id: productId,
			orderIndex: args.orderIndex,
			created: new Date().toISOString(),
			variants: newVariantsFromExistingVariant(args.product.variants),
		};
		await api().products.doc(productId).set(product, { merge: true });
		return product;
	} catch (err) {
		errorHandler.report(err);
		throw err;
	}
};
